/**
 * 设备信息工具类
 * 用于获取设备的唯一标识和相关信息
 */

/**
 * 设备信息接口
 */
export interface DeviceInfo {
    udid: string; // 设备唯一标识
    deviceName: string; // 设备名称
    systemName: string; // 系统名称
    systemVersion: string; // 系统版本
    language: string; // 语言
    timezone: string; // 时区
  }
  
  /**
   * 生成设备唯一标识符 (UDID)
   * 基于多个浏览器特征生成稳定的设备ID
   */
  const generateUDID = (): string => {
  
    // 尝试从localStorage获取已存储的UDID
    const storedUDID = localStorage.getItem("ai-chat-udid");
    if (storedUDID) {
      return storedUDID;
    }
  
    // 收集浏览器特征信息
    const features = [
      navigator.userAgent,
      navigator.language,
      screen.width + "x" + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.platform,
      navigator.cookieEnabled ? "1" : "0",
      typeof navigator.doNotTrack !== "undefined"
        ? navigator.doNotTrack
        : "unknown",
    ];
  
    // 生成基于特征的哈希值
    const fingerprint = features.join("|");
    let hash = 0;
  
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // 转换为32位整数
    }
  
    // 生成UDID格式: aichat-{timestamp}-{hash}
    const timestamp = Date.now().toString(16);
    const hashHex = Math.abs(hash).toString(16);
    const udid = `aichat-${timestamp}-${hashHex}`;
  
    // 存储到localStorage以保持一致性
    localStorage.setItem("ai-chat-udid", udid);
  
    return udid;
  };
  
  /**
   * 获取设备名称
   */
  const getDeviceName = (): string => {

  
    const userAgent = navigator.userAgent;
  
    // 检测移动设备
    if (/iPhone/i.test(userAgent)) {
      const match = userAgent.match(/iPhone OS (\d+)_(\d+)/);
      return match ? `iPhone (iOS ${match[1]}.${match[2]})` : "iPhone";
    }
  
    if (/iPad/i.test(userAgent)) {
      return "iPad";
    }
  
    if (/Android/i.test(userAgent)) {
      const match = userAgent.match(/Android (\d+\.?\d*)/);
      return match ? `Android Device (${match[1]})` : "Android Device";
    }
  
    // 检测桌面系统
    if (/Windows/i.test(userAgent)) {
      if (/Windows NT 10/i.test(userAgent)) return "Windows 10/11 PC";
      if (/Windows NT 6.3/i.test(userAgent)) return "Windows 8.1 PC";
      if (/Windows NT 6.2/i.test(userAgent)) return "Windows 8 PC";
      if (/Windows NT 6.1/i.test(userAgent)) return "Windows 7 PC";
      return "Windows PC";
    }
  
    if (/Mac/i.test(userAgent)) {
      return "Mac";
    }
  
    if (/Linux/i.test(userAgent)) {
      return "Linux PC";
    }
  
    return "Unknown Device";
  };
  
  /**
   * 获取系统名称
   */
  const getSystemName = (): string => {

  
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
  
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return "iOS";
    }
  
    if (/Android/i.test(userAgent)) {
      return "Android";
    }
  
    if (/Windows/i.test(userAgent)) {
      return "Windows";
    }
  
    if (/Mac/i.test(userAgent) && !/iPhone|iPad|iPod/i.test(userAgent)) {
      return "macOS";
    }
  
    if (/Linux/i.test(platform)) {
      return "Linux";
    }
  
    return "Web Browser";
  };
  
  /**
   * 获取系统版本
   */
  const getSystemVersion = (): string => {
  
    const userAgent = navigator.userAgent;
  
    // iOS版本
    const iosMatch = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (iosMatch) {
      return `${iosMatch[1]}.${iosMatch[2]}${
        iosMatch[3] ? "." + iosMatch[3] : ""
      }`;
    }
  
    // Android版本
    const androidMatch = userAgent.match(/Android (\d+\.?\d*\.?\d*)/);
    if (androidMatch && androidMatch[1]) {
      return androidMatch[1];
    }
  
    // Windows版本
    if (/Windows NT 10/i.test(userAgent)) {
      return "10";
    } else if (/Windows NT 6.3/i.test(userAgent)) {
      return "8.1";
    } else if (/Windows NT 6.2/i.test(userAgent)) {
      return "8";
    } else if (/Windows NT 6.1/i.test(userAgent)) {
      return "7";
    }
  
    // macOS版本 (较难准确获取，返回通用信息)
    const macMatch = userAgent.match(/Mac OS X (\d+)_(\d+)_?(\d+)?/);
    if (macMatch) {
      return `${macMatch[1]}.${macMatch[2]}${
        macMatch[3] ? "." + macMatch[3] : ""
      }`;
    }
  
    return "Unknown";
  };
  
  /**
   * 获取语言设置
   */
  const getLanguage = (): string => {

  
    // 优先使用应用内的语言设置
    const appLanguage = localStorage.getItem("nuxt-i18n-locale");
    if (appLanguage) {
      return appLanguage;
    }
  
    // 使用浏览器语言
    return (
      navigator.language ||
      (navigator.languages && navigator.languages[0]) ||
      "en"
    );
  };
  
  /**
   * 获取时区信息
   */
  const getTimezone = (): string => {
    try {
      // 使用 Intl API 获取时区
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      // 降级方案：使用时区偏移
      const offset = new Date().getTimezoneOffset();
      const hours = Math.abs(Math.floor(offset / 60));
      const minutes = Math.abs(offset % 60);
      const sign = offset > 0 ? "-" : "+";
      return `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    }
  };
  
  /**
   * 获取完整的设备信息
   */
  export const getDeviceInfo = (): DeviceInfo => {
    return {
      udid: generateUDID(),
      deviceName: getDeviceName(),
      systemName: getSystemName(),
      systemVersion: getSystemVersion(),
      language: getLanguage(),
      timezone: getTimezone(),
    };
  };
  
  /**
   * 获取设备唯一标识符
   */
  export const getUDID = (): string => {
    return generateUDID();
  };
  
  /**
   * 重新生成设备ID（清除缓存）
   */
  export const regenerateUDID = (): string => {

      localStorage.removeItem("ai-chat-udid");

    return generateUDID();
  };
  
  /**
   * 设备信息工具类
   */
  export const deviceUtils = {
    getDeviceInfo,
    getUDID,
    regenerateUDID,
    getDeviceName,
    getSystemName,
    getSystemVersion,
    getLanguage,
    getTimezone,
  };
  